import { useState } from 'react';
import { motion } from 'framer-motion';
import { Maximize2, BedDouble, Ruler, Wind } from 'lucide-react';
import type { ApartmentType } from '@pullman/shared';
import { useTranslation } from '@/i18n';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { PropertyCardExpanded } from './PropertyCardExpanded';

function formatAMD(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatUSD(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

interface PropertyCardProps {
  property: ApartmentType;
  onCompare?: () => void;
  onAskMore?: (text: string) => void;
}

export function PropertyCard({ property, onCompare, onAskMore }: PropertyCardProps) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [expanded, setExpanded] = useState(false);

  const lowestPrice = property.pricing[0] ?? null;
  const imageUrl = property.render_url || property.floor_plan_url;
  const bedroomLabel =
    property.bedrooms.min === property.bedrooms.max
      ? `${property.bedrooms.min}`
      : `${property.bedrooms.min}-${property.bedrooms.max}`;

  if (expanded) {
    return (
      <PropertyCardExpanded
        property={property}
        onCollapse={() => setExpanded(false)}
        onAskMore={onAskMore}
      />
    );
  }

  return (
    <motion.div
      className="bg-white rounded-card shadow-lg overflow-hidden border border-gray-100"
      initial={reduced ? undefined : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, type: 'spring', damping: 20, stiffness: 200 }}
    >
      {/* Image */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={imageUrl}
          alt={`${property.name} apartment render`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 bg-brand-navy/80 backdrop-blur-sm text-white text-xs font-display tracking-wider px-3 py-1 rounded-full uppercase">
          {property.name}
        </div>
      </div>

      {/* Details */}
      <div className="p-4 space-y-3">
        <h3 className="font-display text-base tracking-wide text-brand-charcoal">
          {property.name} — {bedroomLabel} {t('property.bedrooms')}
        </h3>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-brand-charcoal/70">
          <span className="flex items-center gap-1.5">
            <Ruler size={14} className="text-brand-gold" />
            {property.total_area_sqm} {t('property.sqm')}
          </span>
          <span className="flex items-center gap-1.5">
            <BedDouble size={14} className="text-brand-gold" />
            {bedroomLabel} bed
          </span>
          {property.balcony_area_sqm > 0 && (
            <span className="flex items-center gap-1.5">
              <Wind size={14} className="text-brand-gold" />
              {t('property.balcony')}: {property.balcony_area_sqm} {t('property.sqm')}
            </span>
          )}
        </div>

        {/* Price */}
        <div className="pt-1">
          <p className="text-xs text-brand-charcoal/50 uppercase tracking-wide">{t('property.price')}</p>
          {lowestPrice ? (
            <>
              <p className="text-lg font-semibold text-brand-gold">
                {formatAMD(lowestPrice.price_amd)} AMD
              </p>
              <p className="text-sm text-brand-charcoal/50">
                ~{formatUSD(lowestPrice.price_usd_approx)}
              </p>
            </>
          ) : (
            <p className="text-sm text-brand-charcoal/60 italic">Contact sales for pricing</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => setExpanded(true)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-full
              border border-brand-gold/30 text-brand-charcoal
              hover:bg-brand-gold/10 hover:border-brand-gold/50 transition-all"
          >
            <Maximize2 size={14} />
            {t('property.floorPlan')}
          </button>
          <button
            onClick={onCompare}
            className="flex-1 px-3 py-2 text-sm rounded-full
              border border-brand-gold/30 text-brand-charcoal
              hover:bg-brand-gold/10 hover:border-brand-gold/50 transition-all"
          >
            {t('property.compare')}
          </button>
          <button
            onClick={() => onAskMore?.(`${t('property.askMoreAbout')} ${property.name}`)}
            className="flex-1 px-3 py-2 text-sm rounded-full
              bg-brand-navy text-white
              hover:bg-brand-charcoal transition-all"
          >
            {t('property.askMore')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
